import { supabase } from '../lib/supabaseClient';
import { User } from '../types';

export type ConnectionStatus = 'pending' | 'accepted' | 'rejected' | 'withdrawn' | 'blocked';

export interface TribeConnection {
  id: string;
  requester_id: string;
  addressee_id: string;
  status: ConnectionStatus;
  message: string | null;
  created_at: string;
  updated_at: string;
}

export interface IncomingRequest extends TribeConnection {
  requester: { id: string; username: string; profile_image?: string };
}

export interface OutgoingRequest extends TribeConnection {
  addressee: { id: string; username: string; profile_image?: string };
}

export interface MyTribeMember {
  connectionId: string;
  user: { id: string; username: string; profile_image?: string };
}

/**
 * Sends a Tribe connection request from the current user to another user.
 * Uses an RPC function to handle both new requests and re-requests atomically.
 * @param addresseeId The ID of the user to send the request to.
 * @param message An optional message to include with the request.
 * @returns The created/updated TribeConnection object.
 */
export async function sendConnectionRequest(addresseeId: string, message?: string): Promise<TribeConnection> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .rpc('upsert_tribe_request', { p_addressee: addresseeId, p_message: message ?? null });

  if (error) {
    console.error('Error sending connection request via RPC:', error);
    throw error;
  }
  return data as TribeConnection;
}

/**
 * Retrieves all pending incoming Tribe connection requests for the current user.
 * @returns An array of IncomingRequest objects.
 */
export async function getIncomingRequests(): Promise<IncomingRequest[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('tribe_connections')
    .select('*, requester:profiles!tribe_connections_requester_id_fkey(id, username, profile_image:avatar_url)')
    .eq('addressee_id', user.id)
    .eq('status', 'pending')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching incoming requests:', error);
    throw error;
  }
  return data as IncomingRequest[];
}

/**
 * Retrieves all pending outgoing Tribe connection requests from the current user.
 * @returns An array of OutgoingRequest objects.
 */
export async function getOutgoingRequests(): Promise<OutgoingRequest[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('tribe_connections')
    .select('*, addressee:profiles!tribe_connections_addressee_id_fkey(id, username, profile_image:avatar_url)')
    .eq('requester_id', user.id)
    .eq('status', 'pending')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching outgoing requests:', error);
    throw error;
  }
  return data as OutgoingRequest[];
}

/**
 * Accepts a pending Tribe connection request.
 * @param connectionId The ID of the connection request to accept.
 * @returns The updated TribeConnection object.
 */
export async function acceptConnection(connectionId: string): Promise<TribeConnection> {
  const { data, error } = await supabase
    .from('tribe_connections')
    .update({ status: 'accepted' })
    .eq('id', connectionId)
    .eq('status', 'pending')
    .select()
    .single();
  if (error || !data) throw new Error('Could not accept connection (it may no longer be pending).');
  return data as TribeConnection;
}

/**
 * Rejects a pending Tribe connection request.
 * @param connectionId The ID of the connection request to reject.
 * @returns The updated TribeConnection object.
 */
export async function rejectConnection(connectionId: string): Promise<TribeConnection> {
  const { data, error } = await supabase
    .from('tribe_connections')
    .update({ status: 'rejected' })
    .eq('id', connectionId)
    .eq('status', 'pending')
    .select()
    .single();
  if (error || !data) throw new Error('Could not reject connection (it may no longer be pending).');
  return data as TribeConnection;
}

/**
 * Withdraws a pending outgoing Tribe connection request.
 * @param connectionId The ID of the connection request to withdraw.
 * @returns The updated TribeConnection object.
 */
export async function withdrawRequest(connectionId: string): Promise<TribeConnection> {
  const { data, error } = await supabase
    .from('tribe_connections')
    .update({ status: 'withdrawn' })
    .eq('id', connectionId)
    .eq('status', 'pending')
    .select()
    .single();
  if (error || !data) throw new Error('Could not withdraw request (it may no longer be pending).');
  return data as TribeConnection;
}

/**
 * Retrieves all accepted Tribe members for the current user.
 * @returns An array of MyTribeMember objects.
 */
export async function getMyTribe(): Promise<MyTribeMember[]> {
  const me = (await supabase.auth.getUser()).data.user?.id;
  if (!me) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('tribe_connections')
    .select('id, requester_id, addressee_id, status, requester:profiles!tribe_connections_requester_id_fkey(id, username, profile_image:avatar_url), addressee:profiles!tribe_connections_addressee_id_fkey(id, username, profile_image:avatar_url)')
    .eq('status', 'accepted')
    .or(`requester_id.eq.${me},addressee_id.eq.${me}`);

  if (error) {
    console.error('Error fetching my tribe:', error);
    throw error;
  }

  return (data ?? []).map(row => {
    const other = row.requester_id === me ? row.addressee : row.requester;
    return { connectionId: row.id, user: other };
  });
}

/**
 * Retrieves all connections (pending, accepted, rejected, withdrawn, blocked) for the current user.
 * This is used internally by AuthContext to maintain a comprehensive view of connection states.
 * @returns An array of TribeConnection objects.
 */
export async function getAllMyConnections(): Promise<TribeConnection[]> {
  const me = (await supabase.auth.getUser()).data.user?.id;
  if (!me) throw new Error('Not authenticated');
  const { data, error } = await supabase
    .from('tribe_connections')
    .select('id, requester_id, addressee_id, status, created_at, updated_at, message')
    .or(`requester_id.eq.${me},addressee_id.eq.${me}`);
  if (error) {
    console.error('Error fetching all my connections:', error);
    throw error;
  }
  return data ?? [];
}

/**
 * Retrieves profiles of users who are candidates for a new Tribe connection.
 * Excludes current user and users with existing pending, accepted, blocked, or rejected connections.
 * @param limit Maximum number of candidates to return.
 * @returns An array of User objects.
 */
export async function getBuildCandidates(limit = 50): Promise<User[]> {
  const me = (await supabase.auth.getUser()).data.user?.id;
  if (!me) throw new Error('Not authenticated');

  const { data: edges, error: edgesError } = await supabase
    .from('tribe_connections')
    .select('requester_id, addressee_id, status')
    .or(`requester_id.eq.${me},addressee_id.eq.${me}`);
  if (edgesError) {
    console.error('Error fetching existing connections for candidates:', edgesError);
    throw edgesError;
  }

  const excludedIds = new Set<string>();
  for (const e of edges ?? []) {
    if (['pending', 'accepted', 'blocked', 'rejected'].includes(e.status)) {
      excludedIds.add(e.requester_id === me ? e.addressee_id : e.requester_id);
    }
  }

  const { data: profiles, error: profilesError } = await supabase
    .from('profiles')
    .select('id, username, full_name, avatar_url, bio, interests, location, latitude, longitude, rating, rating_count, created_at')
    .neq('id', me)
    .limit(200);

  if (profilesError) {
    console.error('Error fetching profiles for candidates:', profilesError);
    throw profilesError;
  }

  return (profiles ?? [])
    .filter(p => !excludedIds.has(p.id))
    .map(p => ({
      id: p.id,
      username: p.username || '',
      fullName: p.full_name,
      bio: p.bio,
      interests: p.interests || [],
      location: p.location,
      latitude: p.latitude,
      longitude: p.longitude,
      avatarUrl: p.avatar_url,
      rating: p.rating,
      ratingCount: p.rating_count,
      createdAt: p.created_at,
      email: '', gender: 'prefer-not-to-say', dateOfBirth: null,
      isVerified: false, verificationStatus: 'unverified', subscriptionTier: 'free',
      phone: null, profileVisibility: 'public'
    }))
    .slice(0, limit);
}